import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { Loader2, RefreshCw, AlertTriangle, SearchCheck } from 'lucide-react';
import { fmt, fmtDate, gbErr } from './gbAdminUtils';

const STATUS = {
  PENDING: { t: 'esperando pagamento', c: 'bg-amber-100 text-amber-700' },
  PAID: { t: 'pago (creditando)', c: 'bg-blue-100 text-blue-700' },
  CREDITED: { t: 'moedas na carteira', c: 'bg-emerald-100 text-emerald-700' },
  EXPIRED: { t: 'expirou sem pagar', c: 'bg-slate-100 text-slate-500' },
  REVIEW: { t: 'revisar manualmente', c: 'bg-rose-100 text-rose-700' },
};

export default function GbCoinSales() {
  const [dados, setDados] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [conferindo, setConferindo] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/gb/coin-sales${filtro ? `?status=${filtro}` : ''}`);
      setDados(data);
    } catch (e) { toast.error(gbErr(e)); }
  }, [filtro]);

  useEffect(() => { load(); }, [load]);

  // reconfere no gateway: um pedido específico ou todos os pendentes das últimas 48h
  const reconferir = async (orderId) => {
    setConferindo(orderId || 'todos');
    try {
      const { data } = await api.post(`/admin/gb/coin-sales/recheck${orderId ? `?order_id=${orderId}` : ''}`);
      if (orderId) {
        const st = data.order?.status;
        toast[st === 'CREDITED' ? 'success' : 'info'](
          st === 'CREDITED' ? 'Pago! Moedas creditadas na carteira do cliente.'
            : 'O gateway ainda não confirma pagamento desse pedido.');
      } else {
        toast.success(`${data.conferidos} pedido(s) reconferido(s) · ${data.creditados} creditado(s)`);
      }
      await load();
    } catch (e) { toast.error(gbErr(e)); }
    setConferindo('');
  };

  if (!dados) return <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div>;

  const t = dados.totals || {};
  return (
    <div className="space-y-4" data-testid="gb-admin-coin-sales">
      <div className="rounded-xl border bg-slate-50 p-3 text-xs text-slate-600">
        Pacote único vendido dentro do jogo. As moedas vão para a <b>carteira do evento</b> do cliente
        (nunca para a conta KAKO) e só entram quando o gateway confirma o pagamento, com valor e moeda
        conferidos pelo servidor. <b>PIX pela EFI</b> (Brasil) ou <b>MB WAY pela Stripe</b> (Portugal).
        Todo pedido não creditado das últimas 24h é <b>reconferido no gateway a cada 2 minutos</b> —
        se o aviso do banco não chegar, o próprio sistema confirma e credita. Use
        <b> reconferir agora</b> para checar na hora.
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <div className="rounded-xl border p-3" data-testid="gb-sales-pix">
          <p className="text-[10px] uppercase text-slate-400">vendas por PIX</p>
          <p className="text-base font-black text-slate-700">{t.pix?.n || 0} · R$ {(t.pix?.brl || 0).toFixed(2)}</p>
        </div>
        <div className="rounded-xl border p-3" data-testid="gb-sales-mbway">
          <p className="text-[10px] uppercase text-slate-400">vendas por MB WAY</p>
          <p className="text-base font-black text-slate-700">{t.mbway?.n || 0} · R$ {(t.mbway?.brl || 0).toFixed(2)}</p>
        </div>
        <div className="rounded-xl border p-3">
          <p className="text-[10px] uppercase text-slate-400">moedas entregues</p>
          <p className="text-base font-black text-amber-600">
            {fmt((t.pix?.coins || 0) + (t.mbway?.coins || 0))}
          </p>
        </div>
        <div className="rounded-xl border p-3">
          <p className="text-[10px] uppercase text-slate-400">para revisar</p>
          <p className={`text-base font-black ${dados.review ? 'text-rose-600' : 'text-slate-700'}`}>{dados.review || 0}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={load} data-testid="gb-sales-reload">
          <RefreshCw className="mr-1 h-3.5 w-3.5" />atualizar
        </Button>
        <Button size="sm" onClick={() => reconferir()} disabled={!!conferindo}
          className="bg-orange-500 hover:bg-orange-600" data-testid="gb-sales-recheck-all">
          {conferindo === 'todos' ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            : <SearchCheck className="mr-1 h-3.5 w-3.5" />}reconferir agora
        </Button>
        <select value={filtro} onChange={(e) => setFiltro(e.target.value)} data-testid="gb-sales-filter"
          className="h-9 rounded-lg border px-2 text-xs">
          <option value="">todos os status</option>
          {Object.keys(STATUS).map((k) => <option key={k} value={k}>{STATUS[k].t}</option>)}
        </select>
      </div>

      {(dados.orders || []).length === 0 && (
        <p className="text-xs text-slate-400" data-testid="gb-sales-empty">Nenhuma venda registrada.</p>
      )}
      <div className="space-y-2">
        {(dados.orders || []).map((o) => {
          const st = STATUS[o.status] || STATUS.PENDING;
          return (
            <div key={o.order_id} data-testid={`gb-sales-row-${o.order_id}`}
              className="flex flex-wrap items-center gap-3 rounded-xl border p-2.5">
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${st.c}`}>{st.t}</span>
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-slate-800">{o.nickname}</p>
                <p className="text-[11px] text-slate-500">KAKO {o.kako_id} · {o.order_id}</p>
              </div>
              <p className="text-sm font-black text-amber-600">{fmt(o.coins)} moedas</p>
              <p className="text-[11px] text-slate-500">
                {o.method === 'pix' ? `PIX R$ ${Number(o.amount_brl || 0).toFixed(2)}`
                  : `MB WAY € ${Number(o.amount_eur || 0).toFixed(2)}`}
              </p>
              <p className="text-[11px] text-slate-400">{fmtDate(o.created_at)}</p>
              {o.fail_reason && (
                <span className="flex items-center gap-1 text-[11px] font-bold text-rose-600">
                  <AlertTriangle className="h-3.5 w-3.5" />{o.fail_reason}
                </span>
              )}
              {!o.credited && (
                <Button size="sm" variant="outline" className="ml-auto h-7 text-[11px]"
                  disabled={!!conferindo} onClick={() => reconferir(o.order_id)}
                  data-testid={`gb-sales-recheck-${o.order_id}`}>
                  {conferindo === o.order_id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    : <SearchCheck className="mr-1 h-3 w-3" />}conferir pagamento
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
